import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar
} from '@mui/material';
import {
  People as PeopleIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ApprovedIcon,
  DirectionsCar as CarIcon,
  Assignment as TaskIcon,
  TrendingUp as TrendingIcon,
  CheckCircle
} from '@mui/icons-material';
import { useAdmin } from '../context/AdminContext';
import {
  calculateStats,
  formatDate,
  getStatusColor,
  formatStatusLabel,
} from '../utils/helpers';
import DriverDetailsModal from '../components/DriverDetailsModal';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card 
    sx={{ 
      height: '100%', 
      position: 'relative', 
      overflow: 'visible',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            color="text.secondary" 
            gutterBottom 
            variant="body2"
            sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            component="div" 
            sx={{ fontWeight: 'bold', color, mb: 1, lineHeight: 1.2 }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar
          sx={{
            bgcolor: `${color}15`,
            color: color,
            width: 64,
            height: 64,
            boxShadow: `0 4px 12px ${color}25`,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { drivers, vehicles, tasks } = useAdmin();
  const [selectedDriverForDetails, setSelectedDriverForDetails] = useState(null);
  const [openDriverDetailsModal, setOpenDriverDetailsModal] = useState(false);
  const stats = calculateStats(drivers, vehicles, tasks);

  const toDate = (value) => {
    if (!value) return null;
    if (value?.toDate && typeof value.toDate === 'function') {
      return value.toDate();
    }
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const getDriverIdentifiers = (driver = null) =>
    Array.from(new Set([driver?.userId, driver?.uid, driver?.id].filter(Boolean)));

  const resolveDriverFleetVehicle = (driver = null) => {
    if (!driver) return null;

    if (driver?.ownVehicle) {
      return driver.ownVehicle;
    }

    const assignedVehicleId = driver?.assignedVehicle?.id || driver?.assignedVehicleId;
    if (assignedVehicleId) {
      const vehicleById = vehicles.find((vehicle) => vehicle.id === assignedVehicleId);
      if (vehicleById) {
        return vehicleById;
      }
    }

    const identifiers = new Set(getDriverIdentifiers(driver).map((identifier) => String(identifier)));
    const vehicleByAssignedDriver = vehicles.find((vehicle) => {
      const assignedDriver = String(vehicle?.assignedDriver || '').trim();
      return assignedDriver && identifiers.has(assignedDriver);
    });

    return vehicleByAssignedDriver || driver?.assignedVehicle || null;
  };

  const getNext48HourAvailability = (driver = null) => {
    const now = new Date();
    const next48Hours = new Date(now.getTime() + 48 * 60 * 60 * 1000);
    const scheduleEntries = Array.isArray(driver?.availabilitySchedule)
      ? driver.availabilitySchedule
      : [];

    if (scheduleEntries.length > 0) {
      const availabilityWindows = scheduleEntries
        .filter((entry) => entry?.isAvailable)
        .map((entry) => ({
          start: toDate(entry.startDate) || now,
          end: toDate(entry.endDate),
        }))
        .filter((window) => window.end && window.end > now && window.start <= next48Hours)
        .sort((firstWindow, secondWindow) => firstWindow.start - secondWindow.start);

      if (availabilityWindows.length > 0) {
        const activeWindow = availabilityWindows.find(
          (window) => window.start <= now && window.end > now
        );

        if (activeWindow) {
          return {
            isAvailable: true,
            detail: `Now until ${formatDate(activeWindow.end)}`,
            chipLabel: 'Now',
            chipColor: 'success',
          };
        }

        const nextWindow = availabilityWindows[0];
        return {
          isAvailable: true,
          detail: `Starts ${formatDate(nextWindow.start)}`,
          chipLabel: 'Scheduled',
          chipColor: 'info',
        };
      }

      return {
        isAvailable: false,
        detail: 'No available window in next 48h',
        chipLabel: 'Unavailable',
        chipColor: 'default',
      };
    }

    const availableUntilDate = toDate(driver?.availableUntil);
    if (availableUntilDate && availableUntilDate > now) {
      if (availableUntilDate <= next48Hours) {
        return {
          isAvailable: true,
          detail: `Now until ${formatDate(availableUntilDate)}`,
          chipLabel: 'Limited',
          chipColor: 'warning',
        };
      }

      return {
        isAvailable: true,
        detail: 'Available throughout next 48h',
        chipLabel: 'Next 48h',
        chipColor: 'success',
      };
    }

    return {
      isAvailable: true,
      detail: 'Available throughout next 48h',
      chipLabel: 'Anytime',
      chipColor: 'success',
    };
  };

  // Get recent drivers (last 5)
  const recentDrivers = drivers.slice(0, 5);

  // Get active tasks (last 5)
  const recentTasks = tasks.filter(t => t.status === 'assigned' || t.status === 'in-progress').slice(0, 5);

  // Approved drivers with dynamic fleet + availability in next 48h
  const driversWithFleetAndAvailabilityNext48 = drivers
    .filter((driver) => driver.approved)
    .map((driver) => {
      const fleetVehicle = resolveDriverFleetVehicle(driver);
      const availability = getNext48HourAvailability(driver);
      return {
        driver,
        fleetVehicle,
        availability,
      };
    })
    .filter((entry) => entry.availability.isAvailable);

  const handleOpenDriverDetails = (driver = null) => {
    if (!driver) {
      return;
    }

    setSelectedDriverForDetails(driver);
    setOpenDriverDetailsModal(true);
  };

  const handleCloseDriverDetails = () => {
    setOpenDriverDetailsModal(false);
    setSelectedDriverForDetails(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#000' }}>
          Dashboard Overview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back! Here's what's happening with your drivers and tasks today.
        </Typography>
      </Box>

      {/* Stats Cards - 2 Rows x 3 Columns */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Total Drivers"
            value={stats.totalDrivers}
            icon={<PeopleIcon />}
            color="#DD2424"
            subtitle="Registered drivers"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Pending Approvals"
            value={stats.pendingApprovals}
            icon={<PendingIcon />}
            color="#ff9800"
            subtitle="Awaiting review"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Approved Drivers"
            value={stats.approvedDrivers}
            icon={<ApprovedIcon />}
            color="#000000"
            subtitle="Active drivers"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Available Vehicles"
            value={stats.availableVehicles}
            icon={<CarIcon />}
            color="#DD2424"
            subtitle="Ready to assign"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Available in 48h"
            value={driversWithFleetAndAvailabilityNext48.length}
            icon={<TrendingIcon />}
            color="#000000"
            subtitle="Drivers with upcoming availability"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Active Tasks"
            value={stats.activeTasks}
            icon={<TaskIcon />}
            color="#DD2424"
            subtitle="In progress"
          />
        </Grid>
      </Grid>

      {/* Recent Drivers and Active Tasks - 1 Row x 2 Columns */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Recent Drivers */}
        <Grid item size={{ xs: 12, lg: 6 }}>
          <Paper 
            sx={{ 
              p: 3, 
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PeopleIcon sx={{ mr: 1, color: '#DD2424', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Recent Drivers
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentDrivers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No drivers yet</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentDrivers.map((driver) => (
                      <TableRow key={driver.id} hover sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                        <TableCell sx={{ fontWeight: 500 }}>{driver.name || 'N/A'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{driver.email || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              driver.approved
                                ? 'Approved'
                                : formatStatusLabel(driver.status, 'Pending')
                            }
                            color={getStatusColor(driver.approved ? 'approved' : driver.status)}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Active Tasks */}
        <Grid item size={{ xs: 12, lg: 6 }}>
          <Paper 
            sx={{ 
              p: 3, 
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <TaskIcon sx={{ mr: 1, color: '#DD2424', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Active Tasks
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Driver</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No active tasks</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentTasks.map((task) => {
                      const driver = drivers.find(d => d.id === task.driverId);
                      return (
                        <TableRow key={task.id} hover sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{driver?.name || 'Unknown'}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{task.pickupLocation || task.location || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={formatStatusLabel(task.status, 'Assigned')}
                              color={getStatusColor(task.status)}
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Available Drivers - 1 Row x 1 Column (Full Width) */}
      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, lg: 12 }}>
          <Paper 
            sx={{ 
              p: 4, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ mr: 1.5, color: '#4caf50', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Available Drivers
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {driversWithFleetAndAvailabilityNext48.length} driver(s) available in next 48 hours
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label={`${driversWithFleetAndAvailabilityNext48.length} Available`} 
                color="success" 
                sx={{ fontWeight: 600, px: 1 }}
              />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', borderRadius: '8px 0 0 0' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Fleet Vehicle</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', borderRadius: '0 8px 0 0' }}>Availability (Next 48h)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {driversWithFleetAndAvailabilityNext48.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <PendingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography color="text.secondary" variant="h6">
                            No drivers available in next 48 hours
                          </Typography>
                          <Typography color="text.secondary" variant="body2">
                            Check schedules or update driver availability
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    driversWithFleetAndAvailabilityNext48.map(({ driver, fleetVehicle, availability }) => {
                      const driverProfileImageUrl =
                        driver.profilePictureUrl ||
                        driver.profileImageUrl ||
                        driver.profilePicUrl ||
                        driver.photoURL ||
                        '';
                      
                      return (
                      <TableRow 
                        key={driver.id} 
                        hover 
                        onClick={() => handleOpenDriverDetails(driver)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f9fdf9' },
                          '&:last-child td': { border: 0 }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              src={driverProfileImageUrl}
                              sx={{ 
                                bgcolor: '#f5f5f5', 
                                width: 40, 
                                height: 40, 
                                mr: 2,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                border: '2px solid #e0e0e0'
                              }}
                            >
                              {driver.name?.substring(0, 2).toUpperCase() || 'DR'}
                            </Avatar>
                            <Typography sx={{ fontWeight: 600 }}>
                              {driver.name || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {driver.email || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {driver.phone || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {fleetVehicle ? (
                            <Box>
                              <Chip 
                                icon={<CarIcon />}
                                label={`${fleetVehicle.vehicleType || fleetVehicle.type || 'Vehicle'} - ${fleetVehicle.model || ''}`.trim()} 
                                size="small" 
                                color="info"
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {fleetVehicle.capacity || fleetVehicle.seats || 'N/A'} seats • {fleetVehicle.licensePlate || fleetVehicle.plateNumber || 'N/A'}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip label="Not Assigned" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {availability.detail}
                            </Typography>
                            <Chip 
                              label={availability.chipLabel}
                              size="small" 
                              color={availability.chipColor}
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <DriverDetailsModal
        open={openDriverDetailsModal}
        onClose={handleCloseDriverDetails}
        driver={selectedDriverForDetails}
      />
    </Box>
  );
};

export default Dashboard;
